/** Rotatividade (turnover). */

export interface ResultadoDoTurnover {
  admissoes: number;
  desligamentos: number;
  efetivoInicial: number;
  efetivoFinal: number;
  efetivoMedio: number;
  /** ((admissões + desligamentos) / 2) ÷ efetivo médio — a fórmula mais usada no Brasil. */
  turnoverGeral: number;
  /** Só desligamentos ÷ efetivo médio — mede perda de gente. */
  turnoverDesligamento: number;
}

export interface TurnoverDoPeriodo extends ResultadoDoTurnover {
  /** Demitidos sem data de desligamento: não entram na conta e puxam o número para baixo. */
  inconsistencias: number;
}

export interface MesDoTurnover extends ResultadoDoTurnover {
  mes: string;
}

export interface TurnoverPorSetor {
  departmentId: string | null;
  desligamentos: number;
}

export interface TempoDeCasa {
  mediaGeralDias: number;
  mediaAtivosDias: number;
  mediaDesligadosDias: number;
}

export interface DesligamentosPorNatureza {
  voluntario: number;
  involuntario: number;
  outros: number;
  naoInformado: number;
  total: number;
}

export interface ChurnDeExperiencia {
  saidasAte90d: number;
  totalSaidas: number;
  percentual: number;
}

export interface SetorDetalhado {
  departmentId: number | null;
  nome: string;
  efetivo: number;
  desligamentos: number;
  /** desligamentos ÷ efetivo × 100 — proxy de rotatividade do setor. */
  taxa: number;
  tempoMedioDias: number;
}

/** Dias em texto humano: 18 → "18 d", 200 → "7 meses", 500 → "1a 4m". */
export function tempoLegivel(dias: number): string {
  if (!dias || dias <= 0) return '—';
  if (dias < 30) return `${dias} d`;
  const meses = Math.round(dias / 30.44);
  if (meses < 12) return `${meses} ${meses === 1 ? 'mês' : 'meses'}`;
  const anos = Math.floor(meses / 12);
  const resto = meses % 12;
  return resto ? `${anos}a ${resto}m` : `${anos} ${anos === 1 ? 'ano' : 'anos'}`;
}

const MESES = [
  'jan', 'fev', 'mar', 'abr', 'mai', 'jun',
  'jul', 'ago', 'set', 'out', 'nov', 'dez',
];

/** `2026-03` vira `mar/26`, sem passar por `Date` e sem risco de fuso. */
export function rotuloDoMes(mes: string): string {
  const [ano, mm] = mes.split('-');
  const indice = Number(mm) - 1;
  if (!ano || indice < 0 || indice > 11) return mes;
  return `${MESES[indice]}/${ano.slice(2)}`;
}

export function emPorcento(valor: number): string {
  return `${valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
}

/**
 * A faixa de leitura de uma taxa de rotatividade, que é o que decide a cor dela na tela.
 *
 * Os cortes (20% e 50%) não são novos: a tabela por setor já pintava o chip com eles desde o
 * primeiro corte. Ficam aqui porque o quadro do topo passou a colorir o mesmo tipo de número —
 * e dois lugares decidindo a cor por conta própria é como o mesmo 30% acaba amarelo numa metade
 * da tela e verde na outra.
 */
export type FaixaDaRotatividade = 'alta' | 'atencao' | 'normal';

export function faixaDaRotatividade(taxa: number): FaixaDaRotatividade {
  if (taxa >= 50) return 'alta';
  if (taxa >= 20) return 'atencao';
  return 'normal';
}
